import { api } from './client';
import type {
  AcceptanceStatus,
  Bug,
  BugComment,
  BugImage,
  BugSeverity,
  ChangeRequest,
  FeatureConfirmation,
  FeatureConfirmationImage,
  Page,
} from '../types';
import type {
  BugListParams,
  BugUpsertBody,
  ChangeRequestDetailResponse,
  ChangeRequestListParams,
  ChangeRequestUpdateBody,
  DashboardStats,
  FeatureAcceptanceResponse,
} from '../types/acceptance';

/* ------------------------------- Dashboard --------------------------------- */

export const dashboardApi = {
  /** Số liệu tổng hợp tiến độ nghiệm thu của một dự án (lọc theo giai đoạn nếu có). */
  async get(projectId: string, phaseId?: string): Promise<DashboardStats> {
    const res = await api.get<DashboardStats>(`/projects/${projectId}/dashboard`, {
      params: phaseId ? { phase_id: phaseId } : undefined,
    });
    return res.data;
  },
};

/* ------------------------------ Acceptance ---------------------------------- */

/** Chuẩn hoá phản hồi GET /features/:id/acceptance — chống lệch hợp đồng nhẹ. */
function normalizeAcceptance(raw: unknown): FeatureAcceptanceResponse {
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if ('current' in obj || 'history' in obj) {
      return {
        current: (obj.current as FeatureConfirmation | null) ?? null,
        history: Array.isArray(obj.history) ? (obj.history as FeatureConfirmation[]) : [],
      };
    }
    if (Array.isArray(raw)) {
      const list = raw as FeatureConfirmation[];
      return { current: list[0] ?? null, history: list };
    }
  }
  return { current: null, history: [] };
}

export const acceptanceApi = {
  /** Trạng thái nghiệm thu hiện tại + lịch sử của một feature. */
  async get(featureId: string): Promise<FeatureAcceptanceResponse> {
    const res = await api.get(`/features/${featureId}/acceptance`);
    return normalizeAcceptance(res.data);
  },
  /**
   * Khách hàng xác nhận đã nghiệm thu (permission FEATURE_CONFIRM).
   * Backend POST /api/features/:id/confirm YÊU CẦU field `status` (400 nếu
   * thiếu) — hành động "xác nhận nghiệm thu" tương ứng status 'CONFIRMED'.
   * Cho phép truyền `status` khác (vd luồng tương lai) nhưng mặc định CONFIRMED
   * để không phá vỡ các lời gọi hiện có chỉ truyền (featureId, note).
   */
  async confirm(
    featureId: string,
    note: string,
    status: AcceptanceStatus = 'CONFIRMED',
  ): Promise<FeatureConfirmation> {
    const res = await api.post<FeatureConfirmation>(`/features/${featureId}/confirm`, { status, note });
    return res.data;
  },
  /**
   * Admin thu hồi nghiệm thu (permission FEATURE_CONFIRM_REVOKE) — append 1
   * bản ghi PENDING mới, đưa trạng thái hiện tại về lại "Chờ nghiệm thu".
   */
  async revoke(featureId: string, note?: string): Promise<FeatureConfirmation> {
    const res = await api.post<FeatureConfirmation>(`/features/${featureId}/confirm/revoke`, { note });
    return res.data;
  },
  /** Khách hàng tạo yêu cầu thay đổi (permission CHANGE_REQUEST_CREATE). */
  async createChangeRequest(
    featureId: string,
    title: string,
    description: string,
  ): Promise<ChangeRequest> {
    const res = await api.post<ChangeRequest>(`/features/${featureId}/change-request`, {
      title,
      description,
    });
    return res.data;
  },
};

/* ------------------------ Ảnh của mốc nghiệm thu ---------------------------- */

export const confirmationImagesApi = {
  /** Ảnh đính kèm của một mốc nghiệm thu (permission ACCEPTANCE_VIEW). */
  async list(confirmationId: string): Promise<FeatureConfirmationImage[]> {
    const res = await api.get<{ items: FeatureConfirmationImage[] }>(
      `/confirmations/${confirmationId}/images`,
    );
    return res.data.items;
  },
  /** Upload lần lượt từng ảnh (permission FEATURE_CONFIRM). */
  async upload(confirmationId: string, files: File[]): Promise<FeatureConfirmationImage[]> {
    const uploaded: FeatureConfirmationImage[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<FeatureConfirmationImage>(
        `/confirmations/${confirmationId}/images`,
        form,
      );
      uploaded.push(res.data);
    }
    return uploaded;
  },
  /** Gỡ ảnh — chỉ Admin (permission FEATURE_CONFIRM_REVOKE). */
  async remove(imageId: string): Promise<void> {
    await api.delete(`/confirmation-images/${imageId}`);
  },
};

/* --------------------------- Change requests -------------------------------- */

/**
 * Chuẩn hoá phản hồi GET /change-requests/:id — backend (routes/changeRequests.ts
 * handleGet) trả `{ current, history }` (CR mới nhất trong thread + toàn bộ
 * lịch sử), không có field `request`/`change_request`. Chấp nhận thêm vài
 * hình dạng khác để phòng lệch hợp đồng nhẹ.
 */
function normalizeChangeRequestDetail(raw: unknown): ChangeRequestDetailResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const request = (obj.current ?? obj.request ?? obj.change_request ?? obj) as ChangeRequest;
  const historyRaw = obj.history ?? obj.thread ?? [];
  const history = Array.isArray(historyRaw) ? (historyRaw as ChangeRequest[]) : [];
  return { request, history };
}

export const changeRequestsApi = {
  /** Danh sách change request (lọc theo dự án/giai đoạn/trạng thái). Backend trả `{ items, total, limit, offset }`. */
  async list(params: ChangeRequestListParams = {}): Promise<Page<ChangeRequest>> {
    const res = await api.get<Page<ChangeRequest>>('/change-requests', { params });
    return res.data;
  },
  /** Chi tiết một change request + lịch sử thread. */
  async get(id: string): Promise<ChangeRequestDetailResponse> {
    const res = await api.get(`/change-requests/${id}`);
    return normalizeChangeRequestDetail(res.data);
  },
  /** Nhân viên cập nhật trạng thái xử lý (permission CHANGED_REQUEST_UPDATE/RESOLVE). */
  async update(id: string, body: ChangeRequestUpdateBody): Promise<ChangeRequest> {
    const res = await api.put<ChangeRequest>(`/change-requests/${id}`, body);
    return res.data;
  },
};

/* --------------------------------- Bugs ------------------------------------- */

export const bugsApi = {
  /** Danh sách bug (lọc theo dự án/giai đoạn/tính năng/trạng thái). Backend trả `{ items, total, limit, offset }`. */
  async list(params: BugListParams = {}): Promise<Page<Bug>> {
    const res = await api.get<Page<Bug>>('/bugs', { params });
    return res.data;
  },
  async get(id: string): Promise<Bug> {
    const res = await api.get<Bug>(`/bugs/${id}`);
    return res.data;
  },
  /** Thêm bug cho tính năng (permission BUG_CREATE) — tính năng chuyển sang "Fixing". */
  async create(featureId: string, body: BugUpsertBody): Promise<Bug> {
    const res = await api.post<Bug>(`/features/${featureId}/bugs`, body);
    return res.data;
  },
  /** Sửa tiêu đề/mô tả/trạng thái (permission BUG_UPDATE). */
  async update(id: string, body: BugUpsertBody): Promise<Bug> {
    const res = await api.put<Bug>(`/bugs/${id}`, body);
    return res.data;
  },
  /**
   * Khách/tester xác nhận lỗi không còn xuất hiện (permission BUG_VERIFY).
   * Endpoint riêng, không phải update(): khách có BUG_VERIFY mà không có
   * BUG_UPDATE — xem routes/bugs.ts.
   */
  async verify(id: string): Promise<Bug> {
    const res = await api.post<Bug>(`/bugs/${id}/verify`);
    return res.data;
  },
  /**
   * Mặt đối của verify: khách kiểm tra lại thấy lỗi vẫn còn (permission
   * BUG_VERIFY). Lý do là bắt buộc và được backend lưu thành một bình luận.
   */
  async reopen(id: string, reason: string): Promise<Bug> {
    const res = await api.post<Bug>(`/bugs/${id}/reopen`, { reason });
    return res.data;
  },
  /** Xoá mềm bug (permission BUG_DELETE). */
  async remove(id: string): Promise<void> {
    await api.delete(`/bugs/${id}`);
  },
  /** Ẩn bug khỏi danh sách mặc định — chỉ bug source=TEAM đã đóng (permission BUG_HIDE). */
  async hide(id: string): Promise<Bug> {
    const res = await api.post<Bug>(`/bugs/${id}/hide`);
    return res.data;
  },
  /** Hiện lại bug đã ẩn (permission BUG_HIDE). */
  async unhide(id: string): Promise<Bug> {
    const res = await api.post<Bug>(`/bugs/${id}/unhide`);
    return res.data;
  },
  /**
   * Đổi riêng mức độ nghiêm trọng (permission BUG_SEVERITY_EDIT). Endpoint
   * riêng, không phải update(): người được cấp BUG_SEVERITY_EDIT có thể
   * không có BUG_UPDATE — xem routes/bugs.ts.
   */
  async updateSeverity(id: string, severity: BugSeverity | null): Promise<Bug> {
    const res = await api.post<Bug>(`/bugs/${id}/severity`, { severity });
    return res.data;
  },
};

/* ----------------------------- Ảnh của bug ---------------------------------- */

export const bugImagesApi = {
  /** Ảnh đính kèm của một bug (permission BUG_VIEW). */
  async list(bugId: string): Promise<BugImage[]> {
    const res = await api.get<{ items: BugImage[] }>(`/bugs/${bugId}/images`);
    return res.data.items;
  },
  /**
   * Upload lần lượt từng ảnh (permission BUG_CREATE). Backend nhận
   * multipart/form-data 1 tệp mỗi request — xem routes/bugImages.ts.
   */
  async upload(bugId: string, files: File[]): Promise<BugImage[]> {
    const uploaded: BugImage[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<BugImage>(`/bugs/${bugId}/images`, form);
      uploaded.push(res.data);
    }
    return uploaded;
  },
  /** Xoá mềm ảnh + xoá object R2 (permission BUG_UPDATE). */
  async remove(imageId: string): Promise<void> {
    await api.delete(`/bug-images/${imageId}`);
  },
};

/* --------------------------- Bình luận của bug ------------------------------ */

export const bugCommentsApi = {
  /** Toàn bộ bình luận của một bug, kèm ảnh đính kèm (permission BUG_VIEW). */
  async list(bugId: string): Promise<BugComment[]> {
    const res = await api.get<{ items: BugComment[] }>(`/bugs/${bugId}/comments`);
    return res.data.items;
  },
  /**
   * Gửi bình luận rồi upload ảnh (permission BUG_COMMENT). Phải tạo bình luận
   * trước để có id gắn ảnh vào — backend nhận 1 tệp mỗi request, giống ảnh bug.
   */
  async create(bugId: string, body: string, files: File[] = []): Promise<BugComment> {
    const res = await api.post<BugComment>(`/bugs/${bugId}/comments`, { body });
    const comment = res.data;
    const images: BugImage[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const uploaded = await api.post<BugImage>(`/bug-comments/${comment.id}/images`, form);
      images.push(uploaded.data);
    }
    return { ...comment, images };
  },
};
